import { useEffect, useMemo, useRef, useState } from 'react';

import { BetTooltip } from 'shared/components/ui/bet-tooltip/bet-tooltip';
import { BET_LEGEND_ITEMS } from 'shared/components/ui/bet-tooltip/bet-tooltip.constants';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { coinIcons } from 'shared/constants';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { genericFormatBet } from 'shared/utils/betFormater';
import { getCoinIcon } from 'shared/utils/getCoinIcon';
import { isMobileDevice } from 'shared/utils/isMobileDevice';
import { CurrencyType } from 'shared/web3';

import info from 'assets/png/info.png?format=webp;avif&as=picture';

type CoinSymbol = keyof typeof coinIcons;

interface IActiveBetProps {
  bet: number | null;
  token: `0x${string}`;
  animationsTriggered: boolean;
  streamCurrency?: CurrencyType;
}

export const ActiveBet = ({ bet, token, animationsTriggered, streamCurrency }: IActiveBetProps) => {
  const [betText, setBetText] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [rawBet, setRawBet] = useState<string | null>(null);

  const textContainerRef = useRef<HTMLDivElement>(null);
  const betTextRef = useRef<HTMLParagraphElement>(null);
  const iconAndTooltipRef = useRef<HTMLDivElement>(null);

  const { availableCurrencies } = useCurrenciesStore();

  const currency = useMemo(
    () =>
      availableCurrencies.find((item) => item.address.toLowerCase() === token.toLowerCase()) ||
      streamCurrency,
    [availableCurrencies, token, streamCurrency],
  );

  const shouldShowInfo = useMemo(
    () => BET_LEGEND_ITEMS.some((item) => item.match && betText.includes(item.match)),
    [betText],
  );

  useEffect(() => {
    if (bet && currency) {
      const roomBet = (Number(bet) * 2) / 10 ** currency.decimals;
      const formattedBet = genericFormatBet(roomBet.toString(), 'text-base');

      setBetText(formattedBet.toString());
      setRawBet(roomBet.toString());
    }
  }, [bet, currency]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showTooltip &&
        iconAndTooltipRef.current &&
        !iconAndTooltipRef.current.contains(e.target as Node)
      ) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  return (
    <div
      className={`z-30 h-[20px] w-full min-w-24 rounded-md bg-gradient-to-b from-[#FFF] to-blue-gradient p-[1px] sm:top-[152px] sm:h-[49px] sm:max-w-[385px] sm:rounded-xl lg:absolute lg:bottom-[102px] lg:left-1/2 lg:top-[unset] lg:h-[76px] lg:-translate-x-1/2 lg:transform ${animationsTriggered ? 'animate-slide-in-from-bottom' : 'animate-fast-appear'}`}
      ref={textContainerRef}
    >
      <div
        className="relative flex h-full items-center justify-center gap-1.5 rounded-md bg-gradient-to-t from-[#00011D] to-[#00079C] sm:rounded-xl sm:py-[22px] lg:gap-3"
        ref={betTextRef}
      >
        <span
          className="text-[14px] font-bold leading-none text-white sm:text-[36px]"
          dangerouslySetInnerHTML={{ __html: betText }}
        />
        {getCoinIcon(
          currency?.symbol as CoinSymbol | undefined,
          'h-auto max-h-3.5 w-auto max-w-3.5 sm:max-h-8 sm:max-w-8',
        )}

        {shouldShowInfo && (
          <div className="absolute right-1 sm:top-1" ref={iconAndTooltipRef}>
            <div className="relative flex items-center">
              <OptimizedImage
                imageData={info}
                alt="info"
                className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5"
                onMouseEnter={!isMobileDevice() ? () => setShowTooltip(true) : undefined}
                onMouseLeave={!isMobileDevice() ? () => setShowTooltip(false) : undefined}
                onClick={() => isMobileDevice() && setShowTooltip((prev) => !prev)}
              />
              <BetTooltip
                showTip={showTooltip && shouldShowInfo}
                rawBet={Number(rawBet)}
                containerClassName="absolute right-[-15px] top-[20px] mr-2 min-w-[11.625rem] rounded-xl border border-gray-border bg-border p-[2px] shadow-lg"
                className="flex flex-col gap-1.5 rounded-xl px-3 py-2.5"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
